import { useState } from "react";
import styles from "../styles/runner.module.css";
import type { ReadingCompConfig } from "../types";

interface Props {
  prompt: string;
  config: ReadingCompConfig;
  passingScore: number;
  onScored: (result: {
    score: number;
    passed: boolean;
    raw: { answers: Record<string, string> };
  }) => void;
}

/** Tiny markdown renderer for the passage. Supports # headings, **bold**, paragraphs.
 *  Intentionally minimal — anything richer goes through MDX in the MDX runner.
 */
function renderMarkdownLite(src: string): string {
  // headings
  let html = src
    .replace(/^### (.+)$/gm, "<h3>$1</h3>")
    .replace(/^## (.+)$/gm, "<h2>$1</h2>")
    .replace(/^# (.+)$/gm, "<h1>$1</h1>");
  // bold
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  // paragraphs (split on blank line)
  html = html
    .split(/\n{2,}/)
    .map((block) => (block.startsWith("<h") ? block : `<p>${block.replace(/\n/g, " ")}</p>`))
    .join("");
  return html;
}

export function ReadingComp({ prompt, config, passingScore, onScored }: Props) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [passed, setPassed] = useState(false);

  function pick(qid: string, optId: string) {
    if (submitted) return;
    setAnswers((curr) => ({ ...curr, [qid]: optId }));
  }

  function submit() {
    if (submitted) return;
    if (Object.keys(answers).length !== config.questions.length) return;
    let correct = 0;
    for (const q of config.questions) {
      const picked = answers[q.id];
      const opt = q.options.find((o) => o.id === picked);
      if (opt?.correct) correct += 1;
    }
    const s = config.questions.length === 0 ? 0 : correct / config.questions.length;
    const ok = s >= passingScore;
    setScore(s);
    setPassed(ok);
    setSubmitted(true);
    onScored({
      score: s,
      passed: ok,
      raw: { answers },
    });
  }

  return (
    <div className={styles.col}>
      <h2 className={styles.prompt}>{prompt}</h2>
      <div
        className={styles.passage}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: renderMarkdownLite(config.passage) }}
      />
      {config.questions.map((q) => (
        <div key={q.id} className={styles.col}>
          <h3 className={styles.prompt} style={{ fontSize: 16 }}>
            {q.prompt}
          </h3>
          <ul className={styles.optionList}>
            {q.options.map((o) => {
              const isSelected = answers[q.id] === o.id;
              const cls = [
                styles.optionButton,
                isSelected && !submitted ? styles.optionSelected : "",
                submitted && o.correct ? styles.optionCorrect : "",
                submitted && isSelected && !o.correct ? styles.optionWrong : "",
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <li key={o.id}>
                  <button
                    type="button"
                    className={cls}
                    onClick={() => pick(q.id, o.id)}
                    disabled={submitted}
                  >
                    {o.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      {!submitted ? (
        <button
          type="button"
          className={styles.primaryButton}
          disabled={Object.keys(answers).length !== config.questions.length}
          onClick={submit}
        >
          Validar respuestas
        </button>
      ) : (
        <div className={passed ? styles.summary : `${styles.summary} ${styles.summaryFail}`}>
          <span className={styles.scoreBadge}>{Math.round(score * 100)}%</span>
          <strong>{passed ? "¡Buena lectura!" : "Revisa de nuevo"}</strong>
          <span>Mínimo requerido: {Math.round(passingScore * 100)}%</span>
        </div>
      )}
    </div>
  );
}
